import type { LogoProps } from '@goodfynd/ui';

export enum AppMode {
  ADMIN = 1,
  NORMAL = 0,
  TESTING = 2,
}

export type AppEnvironment = 'local' | 'development' | 'staging' | 'production';
export type AppEnvironmentConfig =
  | DictionaryPartial<AppEnvironment, any>
  | { local: string; development: string; production: never; staging: never };

const currentEnv = (process.env.NEXT_PUBLIC_ENVIRONMENT?.toLowerCase() ||
  'development') as AppEnvironment;
export const selectEnv = <T = string>(envs: AppEnvironmentConfig): T =>
  !!envs[currentEnv] || envs[currentEnv] === false
    ? envs[currentEnv]
    : envs.development;
export const selectNullableEnv = <T = string>(
  envs: AppEnvironmentConfig
): T | null =>
  envs[currentEnv] !== undefined ? envs[currentEnv] : envs.development;

const env = {
  API_HOST: selectEnv({
    local: 'https://apilocal.goodfynd.com:5200',
    development:
      process.env.NEXT_PUBLIC_GOODFYND_API || 'https://api.dev.goodfynd.com',
    staging: 'https://api.stage.goodfynd.com',
    production: 'https://api.goodfynd.com',
  }),
  WEBFLOW_MANAGE_SUBSCRIPTIONS_URL: selectNullableEnv({
    local: 'https://goodfynd.webflow.io/manage/subscriptions',
    development: 'https://goodfynd.webflow.io/manage/subscriptions',
    staging: 'https://goodfynd.webflow.io/manage/subscriptions',
    production: 'https://www.goodfynd.com/manage/subscriptions',
  }),
  APP_HOST: selectEnv({
    development: 'https://local.goodfynd.com',
    staging: 'https://vendors.stage.goodfynd.com',
    production: 'https://vendors.goodfynd.com',
  }),
  BUGSNAG_API_KEY:
    process.env.NEXT_PUBLIC_BUGSNAG_API_KEY ||
    process.env.BUGSNAG_API_KEY ||
    'cca417c239a8075599e004ff5d35d715',
  CONSUMER_APP_HOST: selectEnv({
    development: 'https://local.goodfynd.com:3113',
    staging: 'https://stage.goodfynd.com',
    production: 'https://order.goodfynd.com',
  }),
  COUNTRY_CODE: process.env.COUNTRY_CODE || 'US',
  CURRENCY_CODE: process.env.CURRENCY_CODE || 'USD',
  EMBED_HOST:
    process.env.NEXT_PUBLIC_EMBED_HOST ??
    selectEnv({
      development: 'https://embed.local.goodfynd.com:3113',
      staging: 'https://embed.stage.goodfynd.com',
      production: 'https://embed.goodfynd.com',
    }),
  EVENT_APP_HOST: selectEnv({
    development: 'https://local.goodfynd.com:3113',
    staging: 'https://events.stage.goodfynd.com',
    production: 'https://events.goodfynd.com',
  }),
  FACEBOOK_APP_ID: selectEnv({
    development: '433615357924188',
  }),
  GA_TrackingId: selectEnv({
    development: '',
    staging: '',
    production: '',
  }),
  GOOGLE_API_KEY: selectEnv({
    development: 'AIzaSyBtiBVtgi0IR3XxU7fX3XkXHSbFYcAJYAo',
    production: 'AIzaSyDZNGwMNpb_1xj8jtJ7EEiEN_dRdKgqYYI',
    staging: 'AIzaSyBtiBVtgi0IR3XxU7fX3XkXHSbFYcAJYAo',
  }),
  GOOGLE_CLIENT_ID: selectEnv({
    development:
      '471368377080-lprasrk4o5m5nepfbe2k1nb371qc4j08.apps.googleusercontent.com',
    staging:
      '471368377080-lprasrk4o5m5nepfbe2k1nb371qc4j08.apps.googleusercontent.com', // DEV
    production:
      '860012868371-88cqmj086skojrj5vhhbo17m5uufptgv.apps.googleusercontent.com',
  }),
  GOOGLE_MAPS_ICON: selectEnv({
    development: 'https://stage.goodfynd.com/images/logos/pin.png',
    production: 'https://www.goodfynd.com/images/logos/pin.png',
    staging: 'https://stage.goodfynd.com/images/logos/pin.png',
  }),
  GOOGLE_TAG_MANAGER_AUTH: selectEnv({
    development: 'JoijAdj0PTPoHbGVf8NUSQ',
    production: 'vfd5AE8M6xvzHUjHkYorGA',
    staging: 'VCtjV9-RtsdzeajmZ8GWdQ',
  }),
  GOOGLE_TAG_MANAGER_ENV: selectEnv({
    development: 'env-6',
    production: 'env-3',
    staging: 'env-5',
  }),
  GOOGLE_TAG_MANAGER_ID: 'GTM-5FZ39MN',
  HOT_JAR_SITE_ID: selectEnv({
    development: '',
    staging: '',
    production: '1146447',
  }),
  HUBSPOT_PORTAL_ID: process.env.HUBSPOT_PORTAL_ID || '4404974',
  INVOICE_HOST: selectEnv({
    development: 'https://invoice.local.goodfynd.com:3113',
    staging: 'https://invoice.stage.goodfynd.com',
    production: 'https://invoice.goodfynd.com',
  }),
  LOCALE: process.env.LOCALE || 'en-US',
  LOCALE_SHORT: process.env.LOCALE_SHORT || 'en',
  MARKETING_APP_HOST: selectEnv({
    development: 'https://goodfynd.webflow.io',
    staging: 'https://goodfynd.webflow.io',
    production: 'https://www.goodfynd.com',
  }),
  MODE: selectEnv<number>({
    development: AppMode.ADMIN,
    staging: AppMode.TESTING,
    production: AppMode.NORMAL,
  }),
  NAME: process.env.NEXT_PUBLIC_ENVIRONMENT || 'Development',
  NAME_LOWER: (
    process.env.NEXT_PUBLIC_ENVIRONMENT || 'Development'
  ).toLowerCase() as LogoProps['environment'],
  NAME_SHORT: selectEnv({
    development: 'DEV',
    staging: 'STG',
    production: 'PROD',
  }),
  RECEIPT_HOST: selectEnv({
    local: 'https://apilocal.goodfynd.com:5200',
    development: 'https://api.dev.goodfynd.com',
    staging: 'https://api.stage.goodfynd.com',
    production: 'https://orders.goodfynd.com',
  }),
  STRIPE_CLIENT_ID: selectEnv({
    development: 'ca_FLSw4c4Uvl87O0xgNEtXKmEwrC7t7mTB',
    staging: 'ca_FLSw4c4Uvl87O0xgNEtXKmEwrC7t7mTB',
    production: 'ca_FLSwMw9n0MJadRKCyrk4XwDqU8rU9MuC',
  }),
  STRIPE_PUBLISHABLE_KEY: selectEnv({
    development: 'pk_test_iGUQHAqJsMroMaTcdp4Plxh600XvJvuAfO',
    staging: 'pk_test_iGUQHAqJsMroMaTcdp4Plxh600XvJvuAfO',
    production: 'pk_live_6sUckwUCwC7kZe1NXw11uciS00iywjApoD',
  }),
  STRIPE_PUBLISHABLE_KEY_SAAS: selectEnv({
    development:
      'pk_test_51N9XNaFdujCUOcsGsr9LUBhs8pRQWKzjyk8PLIdaV4VfYdKJ9bl0y9J7axlb5T7FBgH1XGnrK57c4YBtkQQK7qAk00jfDykdrr',
    staging:
      'pk_test_51N9XNaFdujCUOcsGsr9LUBhs8pRQWKzjyk8PLIdaV4VfYdKJ9bl0y9J7axlb5T7FBgH1XGnrK57c4YBtkQQK7qAk00jfDykdrr',
    production:
      'pk_live_51N9XNaFdujCUOcsGpIjzE4Rh1XgPQe6Q6NgPBnIoYXoyHSCRK9SAhygSzBK7yyyv66u8QPxRmPeL6DQ1cuMlsCjz003UsWzp9L',
  }),
  SUPPORT_EMAIL: selectEnv({
    development: 'testers@goodfynd.com',
    staging: 'testers@goodfynd.com',
    production: 'gethelp@goodfynd.com',
  }),
  SALES_EMAIL: selectEnv({
    development: 'testers@goodfynd.com',
    staging: 'vendor.sales@goodfynd.com',
    production: 'vendor.sales@goodfynd.com',
  }),
  HUBSPOT_APP_CLIENT_ID: selectEnv({
    development: '20c83b61-13c2-4388-9d1b-0c0348e7d18e',
    staging: '20c83b61-13c2-4388-9d1b-0c0348e7d18e',
    production: 'a9570aaf-fdac-4fd8-9851-de40e15e6514',
  }),
};

export default env;
